<template>
  <div>
    <RetailAnalyticsHeader @date-change="onDateChange">
      <div class="flex flex-wrap items-center gap-2">
        <SecondaryButton v-if="canExport" :loading="isExporting" @click="exportCSV">
          <DownloadIcon v-if="!isExporting" class="h-4 w-4 mr-2" />
          {{ t("export_button") }}
        </SecondaryButton>
        <template v-if="hasFilters">
          <SearchBarFilter
            v-for="(filterOptions, filterName, filterIdx) in availableFilters"
            :key="filterName"
            :filterName="filterName"
            :filterIdx="filterIdx"
            :available-filters="availableFilters"
            :selected-filters="selectedFilters"
            @updateFilterSearch="updateFilterSearch"
          />
        </template>
      </div>
    </RetailAnalyticsHeader>

    <div v-if="isLoading" class="flex justify-center items-center">
      <Loading />
    </div>
    <template v-else-if="stats">
      <p class="mb-3 text-xs text-gray-400 italic">
        {{ t("click_region_hint") }}
      </p>
      <RegionAnalytics
        :currentMonth="currentMonth"
        :stats="stats"
        :total="total"
        :showGrossSalesCount="true"
        :showActiveStoresRate="true"
        :order="order"
        @region-selected="onRegionSelected"
        @sort="onSort"
      />
    </template>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { mapActions, mapGetters } from "vuex";
import { Loading, SecondaryButton, SearchBarFilter, download } from "@estaly/ui";
import { DownloadIcon } from "@heroicons/vue/outline";
import RetailAnalyticsHeader from "@/components/analytics/retail/RetailAnalyticsHeader.vue";
import RegionAnalytics from "@/components/analytics/retail/RegionAnalytics.vue";
import AnalyticsService from "@/api/services/analytics";
import { useServerSort } from "@/composables/useServerSort";

export default {
  components: {
    RetailAnalyticsHeader,
    RegionAnalytics,
    Loading,
    SecondaryButton,
    SearchBarFilter,
    DownloadIcon,
  },

  setup() {
    const { t } = useI18n();
    const { effectiveSortColumn, sortDirection, order, handleSort } = useServerSort("gross_sales_count");
    return { t, effectiveSortColumn, sortDirection, order, handleSort };
  },

  data: () => {
    return {
      isLoading: false,
      isExporting: false,
      startDate: null,
      endDate: null,
      currentMonth: "",
      stats: null,
      total: null,
      selectedFilters: {
        region: [],
      },
    };
  },

  computed: {
    ...mapGetters("organization", ["regions"]),
    ...mapGetters("auth", ["isOrganizationAdmin", "isStoreAdmin", "isManager"]),

    canExport() {
      return this.isOrganizationAdmin || this.isStoreAdmin || this.isManager;
    },

    regionFilterOptions() {
      return this.regions.map((region) => ({ value: region, label: region }));
    },

    availableFilters() {
      const filters = {};
      if (this.regionFilterOptions.length) {
        filters.region = this.regionFilterOptions;
      }
      return filters;
    },

    hasFilters() {
      return Object.keys(this.availableFilters).length > 0;
    },

    exportIncludeColumns() {
      return [];
    },
  },

  created() {
    this.fetchOrganization();
  },

  methods: {
    ...mapActions("organization", { fetchOrganization: "fetch" }),
    ...mapActions("notifications", ["notify"]),

    async exportCSV() {
      if (!this.startDate || !this.endDate) return;
      this.isExporting = true;
      try {
        const response = await AnalyticsService.exportRetailRegions(
          this.startDate,
          this.endDate,
          [],
          this.selectedFilters.region,
          this.effectiveSortColumn,
          this.sortDirection,
          this.exportIncludeColumns,
        );

        if (response?.status === 204) {
          return this.notify({ category: "simple", type: "error", title: this.t("no_data_to_export") });
        }

        if (!response?.success) return;

        download(response.data, "retail_analytics_regions.csv");
      } finally {
        this.isExporting = false;
      }
    },

    onDateChange({ start, end, currentMonth }) {
      this.startDate = start;
      this.endDate = end;
      this.currentMonth = currentMonth;
      this.getAnalytics();
    },

    async getAnalytics() {
      if (!this.startDate || !this.endDate) return;
      if (this.isLoading) return;

      this.isLoading = true;

      const response = await AnalyticsService.getRetailRegionsData(
        this.startDate,
        this.endDate,
        [],
        this.selectedFilters.region,
        this.effectiveSortColumn,
        this.sortDirection,
      );

      this.isLoading = false;
      if (response?.success) {
        const data = response.data;
        this.stats = data.stats;
        this.total = data.total;
      }
    },

    onSort(column) {
      if (this.isLoading) return;
      this.handleSort(column);
      this.getAnalytics();
    },

    updateFilterSearch(filterName, value) {
      this.selectedFilters[filterName] = value;
      this.getAnalytics();
    },

    onRegionSelected(regionName) {
      this.$router.push({ path: "/admin/analytics/retail", query: { region: regionName } });
    },
  },
};
</script>

<i18n src="./RegionRetailAnalytics.i18n.json"></i18n>
